import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileSearch,
  ShieldAlert,
  XCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types — mirror TrusteeReconciliationService DTOs
// ---------------------------------------------------------------------------

type ReconStatus = 'PASS' | 'WARN' | 'FAIL' | 'MISSING' | 'TRUSTEE_ONLY';

interface DealReconSummary {
  dealId: number;
  dealName: string;
  manager: string | null;
  latestReportDate: string | null;
  reportCount: number;
  metricCount: number;
  passCount: number;
  warnCount: number;
  failCount: number;
  missingCount?: number | null;
  maxAbsDelta: number;
  status: ReconStatus;
  hasCollateralBasis?: boolean | null;
  hasDebtTrancheBasis?: boolean | null;
}

interface MetricRow {
  metric: string;            // WARF | WAL | OC | IC
  tranche: string | null;
  description: string;
  ourValue: number | null;
  trusteeValue: number | null;
  delta: number | null;
  deltaNormalised: number | null;
  status: ReconStatus;
  unit: string;
  trigger?: number | null;
  ourPassed?: boolean | null;
}

interface DealRecon {
  dealId: number;
  dealName: string;
  manager: string | null;
  latestReportDate: string | null;
  trusteeWarf: number | null;
  trusteeWal: number | null;
  ourWarf: number | null;
  ourWal: number | null;
  sofrAssumed: number;
  metrics: MetricRow[];
  hasCollateralBasis?: boolean | null;
  hasDebtTrancheBasis?: boolean | null;
  basisNote?: string | null;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Crumb = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #60a5fa;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.5rem;

  &:hover { color: #93c5fd; }
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0.25rem 0 0 0;
  max-width: 760px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(motion.div)<{ $accent: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: ${p => p.$accent};
  }
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.35rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const StatHint = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  margin-top: 0.2rem;
`;

const Panel = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const PanelTitle = styled.h3`
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelHint = styled.span`
  color: #64748b;
  font-size: 0.72rem;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;
`;

const Th = styled.th<{ $numeric?: boolean }>`
  color: #94a3b8;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.75rem 1rem;
  text-align: ${p => p.$numeric ? 'right' : 'left'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(15, 23, 42, 0.4);
  white-space: nowrap;
`;

const Tr = styled.tr<{ $clickable?: boolean }>`
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: background 0.15s;
  &:hover {
    background: ${p => p.$clickable ? 'rgba(59, 130, 246, 0.08)' : 'transparent'};
  }
`;

const Td = styled.td<{ $numeric?: boolean; $muted?: boolean }>`
  color: ${p => p.$muted ? '#94a3b8' : '#e2e8f0'};
  font-size: 0.82rem;
  padding: 0.7rem 1rem;
  text-align: ${p => p.$numeric ? 'right' : 'left'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ $status: ReconStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: ${p => statusBg(p.$status)};
  color: ${p => statusFg(p.$status)};
  border: 1px solid ${p => statusBorder(p.$status)};
`;

const DeltaCell = styled.span<{ $status: ReconStatus }>`
  color: ${p => statusFg(p.$status)};
  font-weight: 600;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  padding: 3rem 2rem;
  text-align: center;
`;

const ErrorState = styled.div`
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
`;

const BasisBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #c7d2fe;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 0.85rem 1.1rem;
  margin-bottom: 1.25rem;
  font-size: 0.8rem;
  line-height: 1.5;

  svg { flex-shrink: 0; margin-top: 2px; color: #a5b4fc; }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBg(s: ReconStatus): string {
  if (s === 'FAIL')         return 'rgba(239, 68, 68, 0.15)';
  if (s === 'WARN')         return 'rgba(245, 158, 11, 0.15)';
  if (s === 'PASS')         return 'rgba(16, 185, 129, 0.15)';
  if (s === 'TRUSTEE_ONLY') return 'rgba(99, 102, 241, 0.12)';
  return 'rgba(148, 163, 184, 0.15)';
}
function statusFg(s: ReconStatus): string {
  if (s === 'FAIL')         return '#ef4444';
  if (s === 'WARN')         return '#f59e0b';
  if (s === 'PASS')         return '#10b981';
  if (s === 'TRUSTEE_ONLY') return '#a5b4fc';
  return '#94a3b8';
}
function statusBorder(s: ReconStatus): string {
  if (s === 'FAIL')         return 'rgba(239, 68, 68, 0.4)';
  if (s === 'WARN')         return 'rgba(245, 158, 11, 0.4)';
  if (s === 'PASS')         return 'rgba(16, 185, 129, 0.4)';
  if (s === 'TRUSTEE_ONLY') return 'rgba(99, 102, 241, 0.35)';
  return 'rgba(148, 163, 184, 0.3)';
}
function statusIcon(s: ReconStatus) {
  if (s === 'FAIL') return <XCircle size={11} />;
  if (s === 'WARN') return <AlertTriangle size={11} />;
  if (s === 'PASS') return <CheckCircle2 size={11} />;
  if (s === 'TRUSTEE_ONLY') return <FileSearch size={11} />;
  return null;
}
function statusLabel(s: ReconStatus): string {
  return s === 'TRUSTEE_ONLY' ? 'TRUSTEE ONLY' : s;
}

const fmtNum = (n: number | null | undefined, dp = 4): string => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

const fmtSigned = (n: number | null | undefined, dp = 4): string => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '-';
  return sign + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================

const TrusteeReconciliationScreen: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dealId = searchParams.get('deal');

  if (dealId) {
    return (
      <DealReconView
        dealId={Number(dealId)}
        onBack={() => {
          const next = new URLSearchParams(searchParams);
          next.delete('deal');
          setSearchParams(next, { replace: true });
        }}
      />
    );
  }

  return (
    <DealReconList
      onSelect={(id) => {
        const next = new URLSearchParams(searchParams);
        next.set('deal', String(id));
        setSearchParams(next, { replace: true });
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// List view — per-deal exception queue
// ---------------------------------------------------------------------------

const DealReconList: React.FC<{ onSelect: (id: number) => void }> = ({ onSelect }) => {
  const [rows, setRows] = useState<DealReconSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setErr(null);
    axios.get<DealReconSummary[]>('/api/credit/clo/trustee-recon')
      .then(res => { if (alive) setRows(res.data || []); })
      .catch(e => {
        if (alive) setErr(e?.response?.data?.message || e.message || 'Failed to load recon');
      });
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    if (!rows) return null;
    return {
      deals: rows.length,
      fails: rows.filter(r => r.status === 'FAIL').length,
      warns: rows.filter(r => r.status === 'WARN').length,
      passes: rows.filter(r => r.status === 'PASS').length,
    };
  }, [rows]);

  return (
    <Container>
      <Header>
        <Title>Trustee Reconciliation</Title>
        <Subtitle>
          Independent recalc of WARF, WAL, and per-tranche OC/IC tests vs the
          latest trustee snapshot ingested for each CLO deal. Failures and warnings
          surface first — click a deal to drill into the metric-level deltas.
        </Subtitle>
      </Header>

      {stats && (
        <StatsGrid>
          <StatCard $accent="#3b82f6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <StatLabel>Deals Recon'd</StatLabel>
            <StatValue>{stats.deals}</StatValue>
            <StatHint>with at least one trustee report</StatHint>
          </StatCard>
          <StatCard $accent="#ef4444" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <StatLabel>Failing Deals</StatLabel>
            <StatValue style={{ color: stats.fails > 0 ? '#ef4444' : 'white' }}>{stats.fails}</StatValue>
            <StatHint>any metric beyond FAIL tolerance</StatHint>
          </StatCard>
          <StatCard $accent="#f59e0b" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <StatLabel>Warning Deals</StatLabel>
            <StatValue style={{ color: stats.warns > 0 ? '#f59e0b' : 'white' }}>{stats.warns}</StatValue>
            <StatHint>variance within tolerable band</StatHint>
          </StatCard>
          <StatCard $accent="#10b981" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <StatLabel>Clean Deals</StatLabel>
            <StatValue>{stats.passes}</StatValue>
            <StatHint>all metrics within tolerance</StatHint>
          </StatCard>
        </StatsGrid>
      )}

      {err && <ErrorState><ShieldAlert size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{err}</ErrorState>}

      <Panel>
        <PanelHeader>
          <PanelTitle><FileSearch size={16} />Per-Deal Exception Queue</PanelTitle>
          <PanelHint>{rows ? `${rows.length} ${rows.length === 1 ? 'deal' : 'deals'}` : ''}</PanelHint>
        </PanelHeader>

        {rows === null && <EmptyState>Loading recon…</EmptyState>}
        {rows !== null && rows.length === 0 && (
          <EmptyState>
            No CLO deals have an ingested trustee report yet. Upload a trustee
            distribution notice to populate this queue.
          </EmptyState>
        )}

        {rows !== null && rows.length > 0 && (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Deal</Th>
                  <Th>Manager</Th>
                  <Th>Latest Report</Th>
                  <Th>Status</Th>
                  <Th $numeric>Fail</Th>
                  <Th $numeric>Warn</Th>
                  <Th $numeric>Pass</Th>
                  <Th $numeric>Max Δ (norm.)</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <Tr key={r.dealId} $clickable onClick={() => onSelect(r.dealId)}>
                    <Td><strong style={{ color: 'white' }}>{r.dealName}</strong></Td>
                    <Td $muted>{r.manager || '—'}</Td>
                    <Td $muted>{r.latestReportDate || '—'}</Td>
                    <Td>
                      <StatusBadge $status={r.status}>
                        {statusIcon(r.status)}{statusLabel(r.status)}
                      </StatusBadge>
                    </Td>
                    <Td $numeric>
                      {r.failCount > 0
                        ? <span style={{ color: '#ef4444', fontWeight: 700 }}>{r.failCount}</span>
                        : <span style={{ color: '#475569' }}>—</span>}
                    </Td>
                    <Td $numeric>
                      {r.warnCount > 0
                        ? <span style={{ color: '#f59e0b', fontWeight: 700 }}>{r.warnCount}</span>
                        : <span style={{ color: '#475569' }}>—</span>}
                    </Td>
                    <Td $numeric $muted>{r.passCount}</Td>
                    <Td $numeric>
                      <span style={{ color: r.maxAbsDelta >= 1 ? '#ef4444' : r.maxAbsDelta >= 0.3 ? '#f59e0b' : '#94a3b8' }}>
                        {fmtNum(r.maxAbsDelta, 3)}×
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Panel>
    </Container>
  );
};

// ---------------------------------------------------------------------------
// Detail view — one deal, every metric
// ---------------------------------------------------------------------------

const DealReconView: React.FC<{ dealId: number; onBack: () => void }> = ({ dealId, onBack }) => {
  const [detail, setDetail] = useState<DealRecon | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setErr(null);
    axios.get<DealRecon>(`/api/credit/clo/trustee-recon/${dealId}`)
      .then(res => { if (alive) setDetail(res.data); })
      .catch(e => {
        if (alive) setErr(e?.response?.status === 404
          ? 'Deal not found or no trustee report on file.'
          : e?.response?.data?.message || e.message || 'Failed to load recon');
      });
    return () => { alive = false; };
  }, [dealId]);

  const grouped = useMemo(() => {
    if (!detail) return { deal: [] as MetricRow[], oc: [] as MetricRow[], ic: [] as MetricRow[] };
    return {
      deal: detail.metrics.filter(m => !m.tranche),
      oc: detail.metrics.filter(m => m.metric === 'OC' && m.tranche),
      ic: detail.metrics.filter(m => m.metric === 'IC' && m.tranche),
    };
  }, [detail]);

  return (
    <Container>
      <Header>
        <Crumb onClick={onBack}><ArrowLeft size={12} />Back to exception queue</Crumb>
        <Title>{detail?.dealName || '…'}</Title>
        <Subtitle>
          {detail?.manager ? `Manager: ${detail.manager} · ` : ''}
          Latest trustee report: {detail?.latestReportDate || '—'}
          {detail?.sofrAssumed != null && ` · SOFR assumed: ${(detail.sofrAssumed * 100).toFixed(2)}%`}
        </Subtitle>
      </Header>

      {err && <ErrorState><ShieldAlert size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{err}</ErrorState>}
      {!detail && !err && <EmptyState>Loading deal detail…</EmptyState>}

      {detail?.basisNote && (
        <BasisBanner>
          <FileSearch size={14} />
          <span>{detail.basisNote}</span>
        </BasisBanner>
      )}

      {detail && (
        <>
          <Panel style={{ marginBottom: '1.25rem' }}>
            <PanelHeader>
              <PanelTitle>Deal-Level Quality Tests</PanelTitle>
              <PanelHint>{grouped.deal.length} metrics</PanelHint>
            </PanelHeader>
            <MetricTable rows={grouped.deal} />
          </Panel>

          <Panel style={{ marginBottom: '1.25rem' }}>
            <PanelHeader>
              <PanelTitle>Overcollateralization (OC) — Per Tranche</PanelTitle>
              <PanelHint>{grouped.oc.length} tranches</PanelHint>
            </PanelHeader>
            <MetricTable rows={grouped.oc} showTrigger />
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Interest Coverage (IC) — Per Tranche</PanelTitle>
              <PanelHint>{grouped.ic.length} tranches</PanelHint>
            </PanelHeader>
            <MetricTable rows={grouped.ic} showTrigger />
          </Panel>
        </>
      )}
    </Container>
  );
};

// ---------------------------------------------------------------------------
// Metric comparison table — shared between deal-level + OC + IC
// ---------------------------------------------------------------------------

const MetricTable: React.FC<{ rows: MetricRow[]; showTrigger?: boolean }> = ({ rows, showTrigger }) => {
  if (rows.length === 0) {
    return <EmptyState>No metrics in this group.</EmptyState>;
  }
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <Th>{showTrigger ? 'Tranche' : 'Metric'}</Th>
            <Th>Description</Th>
            <Th $numeric>Our Value</Th>
            <Th $numeric>Trustee</Th>
            <Th $numeric>Δ</Th>
            {showTrigger && <Th $numeric>Trigger</Th>}
            {showTrigger && <Th>Our Pass/Fail</Th>}
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, idx) => {
            const dp = m.metric === 'WARF' ? 2 : 4;
            return (
              <tr key={`${m.metric}-${m.tranche || 'deal'}-${idx}`}>
                <Td><strong style={{ color: 'white' }}>{showTrigger ? m.tranche : m.metric}</strong></Td>
                <Td $muted>{m.description}</Td>
                <Td $numeric>{fmtNum(m.ourValue, dp)}</Td>
                <Td $numeric $muted>{fmtNum(m.trusteeValue, dp)}</Td>
                <Td $numeric>
                  <DeltaCell $status={m.status}>{fmtSigned(m.delta, dp)}</DeltaCell>
                </Td>
                {showTrigger && <Td $numeric $muted>{fmtNum(m.trigger, dp)}</Td>}
                {showTrigger && (
                  <Td>
                    {m.ourPassed === true
                      ? <span style={{ color: '#10b981', fontWeight: 600 }}>PASS</span>
                      : m.ourPassed === false
                        ? <span style={{ color: '#ef4444', fontWeight: 600 }}>FAIL</span>
                        : '—'}
                  </Td>
                )}
                <Td>
                  <StatusBadge $status={m.status}>
                    {statusIcon(m.status)}{statusLabel(m.status)}
                  </StatusBadge>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default TrusteeReconciliationScreen;
